import { ArrowRight, Bath, BedDouble, MapPin, Maximize } from "lucide-react";

type PropertyListingCardProps = {
  id: string;
  title: string;
  location: string;
  price: string;
  bedrooms: number;
  bathrooms: number;
  area: string;
  image: string;
  features: string[];
  statusLabel: string;
  categoryLabel: string;
  href?: string;
};

const propertyTypes = [
  ["penthouse", "Penthouse"],
  ["apartment", "Apartment"],
  ["villa", "Villa"],
  ["townhouse", "Townhouse"],
  ["studio", "Studio"],
  ["suite", "Suite"],
  ["home", "Home"],
  ["residence", "Residence"],
  ["tower", "Apartment"],
] as const;

const formatPrice = (price: string) => {
  // Parse the price string and format with K/M notation
  const match = price.match(/KES\s*([\d,]+)/);
  if (!match) return price;
  
  const numericPrice = parseInt(match[1].replace(/,/g, ''));
  
  if (numericPrice >= 1000000) {
    const millions = (numericPrice / 1000000).toFixed(1);
    return `${millions}M`;
  } else if (numericPrice >= 1000) {
    const thousands = (numericPrice / 1000).toFixed(0);
    return `${thousands}K`;
  }
  
  return price;
};

const inferPropertyType = (title: string) => {
  const normalizedTitle = title.toLowerCase();
  const match = propertyTypes.find(([keyword]) => normalizedTitle.includes(keyword));

  return match?.[1] ?? "Property";
};

const PropertyListingCard = ({
  id,
  title,
  location,
  price,
  bedrooms,
  bathrooms,
  area,
  image,
  statusLabel,
  href = "#",
}: PropertyListingCardProps) => {
  const isSale = statusLabel.toLowerCase().includes("sale");
  const statusClasses = isSale ? "bg-foreground text-background" : "bg-primary text-primary-foreground";
  const formattedPrice = formatPrice(price);
  const targetHref = href === "#" ? `/property/${id}` : href;

  return (
    <a href={targetHref} className="block group">
      <div className="overflow-hidden rounded-2xl border border-border bg-[#FFFFFF] shadow-sm transition-shadow duration-300 hover:shadow-lg">
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={image}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>

        <div className="bg-[#FFFFFF] px-4 py-4 md:px-5 md:py-5">
          <div className="mb-3 flex flex-wrap gap-2">
            <span className="rounded-full bg-accent px-3 py-1 font-body text-xs font-medium text-accent-foreground">
              {inferPropertyType(title)}
            </span>
            <span className={`rounded-full px-3 py-1 font-body text-xs font-medium ${statusClasses}`}>
              {statusLabel}
            </span>
          </div>

          <h3 className="mb-2 min-h-[3rem] font-body text-base font-semibold uppercase leading-tight text-foreground line-clamp-2">
            {title}
          </h3>

          <div className="mb-3 flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <span className="font-body text-sm">{location}</span>
          </div>

          {/* Prominent Price Display */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-3 text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <BedDouble className="h-4 w-4" />
                <span className="font-body text-sm">{bedrooms} Beds</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Bath className="h-4 w-4" />
                <span className="font-body text-sm">{bathrooms} Baths</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Maximize className="h-4 w-4" />
                <span className="font-body text-sm">{area}</span>
              </div>
            </div>
          </div>

          {/* Price as main focal point */}
          <div className="mb-4 flex items-center justify-between border-t border-border pt-3">
            <span className="font-body text-xl font-bold text-foreground md:text-2xl">
              {formattedPrice}
            </span>
            <span className="font-body text-xs text-muted-foreground">
              {isSale ? "Total Price" : "Per Month"}
            </span>
          </div>

          <div className="inline-flex items-center gap-2 font-body text-sm text-accent transition-colors hover:text-accent/80">
            View Details
            <ArrowRight className="h-4 w-4" />
          </div>
        </div>
      </div>
    </a>
  );
};

export default PropertyListingCard;